import { toast } from 'react-toastify';
import { useState, useEffect, useRef } from 'react';
import DirectInput from '../../../../components/directInput/DirectInput';
import emailValid from '../../../../utils/emailValid';
import phoneValid from '../../../../utils/phoneValid';
import errorHandle from '../../../../utils/errorHandle';
import { orgTripRegOTP } from '../../../../api/orgTripAPI';
import { orgGuRegOTP } from '../../../../api/orgGuidAPI';
import { orgCampRegOTP } from '../../../../api/orgCampAPI';
import { orgActRegOTP } from '../../../../api/orgActAPI';

export default function RegOtp({ formData, setFormData, setLoading, otpStatus, setOTPStatus }) {
    const [resendTimer, setResendTimer] = useState(0);
    const [otpExpired, setOtpExpired] = useState(false);
    const [otpError, setOtpError] = useState('');

    // useRef instead of useState — avoids unnecessary re-renders for timer IDs
    const resendIntervalRef = useRef(null);
    const otpExpiryTimeoutRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
            if (otpExpiryTimeoutRef.current) clearTimeout(otpExpiryTimeoutRef.current);
        };
    }, []);

    function startResendTimer(duration = 120) {
        if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
        setResendTimer(duration);

        resendIntervalRef.current = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(resendIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }

    function startOtpExpiryTimer() {
        if (otpExpiryTimeoutRef.current) clearTimeout(otpExpiryTimeoutRef.current);

        otpExpiryTimeoutRef.current = setTimeout(
            () => {
                setOtpExpired(true);
                setOtpError('OTP has expired. Please generate a new OTP.');
            },
            10 * 60 * 1000
        );
    }

    async function genOTP(method) {
        if (!phoneValid(formData.phoneNumber)) {
            toast.error('Enter a valid phone number');
            return;
        }
        if (!emailValid(formData.emailId)) {
            toast.error('Enter a valid email');
            return;
        }
        if (!formData.type || formData.type === 'Select' || formData.type === '-1') {
            toast.error('Please select a type');
            return;
        }

        const payload = {
            emailId: formData.emailId,
            phoneNumber: formData.phoneNumber,
            countryCode: formData.countryCode,
            type: formData.type
        };

        try {
            setLoading(true);
            let res;

            // FIX: added 'trip' case, removed unused dead branches
            if (formData.type === 'trip') {
                res = await orgTripRegOTP(payload, method);
            } else if (formData.type === 'camp') {
                res = await orgCampRegOTP(payload, method);
            } else if (formData.type === 'guide') {
                res = await orgGuRegOTP(payload, method);
            } else if (formData.type === 'activity') {
                res = await orgActRegOTP(payload, method);
            } else {
                toast.error('Unknown type selected');
                return;
            }

            if (res.status === 200) {
                setOTPStatus(true);
                setOtpError('');
                setOtpExpired(false);
                startResendTimer(120);
                startOtpExpiryTimer(); // FIX: only start expiry timer on success
                toast.success(`OTP sent to ${formData.phoneNumber} and ${formData.emailId}`);
            }
        } catch (error) {
            errorHandle(error);
        } finally {
            setLoading(false);
        }
    }

    // FIX: Math.floor instead of Math.round for correct MM:SS display
    const minutes = Math.floor(resendTimer / 60);
    const seconds = String(resendTimer % 60).padStart(2, '0');

    const canGenerate = formData.phoneNumber && formData.emailId;

    return (
        <div>
            <h2 className='orgRegisterH2'>OTP</h2>
            <div className='orgRegisterSecDiv'>
                {otpError && <span className='orgLoginError'>{otpError}</span>}
                <br />

                {!otpStatus || otpExpired ? (
                    // Show Generate / Re-Generate button
                    <button
                        type='button'
                        className={canGenerate ? 'orgRegisterAddBtn blueBtn' : ''}
                        disabled={!canGenerate}
                        onClick={() => genOTP(otpExpired ? 'register' : 'resend')}
                    >
                        {otpExpired ? 'Re-Generate OTP' : 'Generate OTP'}
                    </button>
                ) : resendTimer > 0 ? (
                    // Countdown timer
                    <p className='resend-timer'>
                        Resend OTP in {minutes}:{seconds}
                    </p>
                ) : (
                    // Resend button
                    <button className='resend-btn' onClick={() => genOTP('resend')}>
                        Resend OTP
                    </button>
                )}

                {canGenerate && (
                    <p className='regOTPP'>
                        OTP will be sent to {formData.phoneNumber} and {formData.emailId}
                    </p>
                )}

                {otpStatus && !otpExpired && (
                    <>
                        <DirectInput
                            label='Phone OTP'
                            name='phoneOTP'
                            value={formData.phoneOTP}
                            type='number'
                            placeholder=''
                            setFormData={setFormData}
                            min={6}
                            limit={6}
                            nest=''
                        />
                        <DirectInput
                            label='Email OTP'
                            name='emailOTP'
                            value={formData.emailOTP}
                            type='number'
                            placeholder=''
                            setFormData={setFormData}
                            min={6}
                            limit={6}
                            nest=''
                        />
                    </>
                )}
            </div>
        </div>
    );
}
